import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Home, Building, Users, AlertCircle } from 'lucide-react';
import { ApplicationFormData, ApplicationFormErrors } from '@/types/application';
import { agentApi } from '@/services/agentApi';
import { SearchableCountryDropdown } from '../../../../../shared/src/components/ui/SearchableCountryDropdown';

interface ContactInfoStepProps {
  formData: ApplicationFormData;
  errors: ApplicationFormErrors;
  updateFormData: (updates: Partial<ApplicationFormData>) => void;
  setErrors: (errors: ApplicationFormErrors) => void;
}

const ACCOMMODATION_TYPES = [
  'Own Home',
  'Rental',
  'Family/Friends',
  'Student Accommodation',
  'Hostel',
  'Other'
];

export const ContactInfoStep: React.FC<ContactInfoStepProps> = ({
  formData,
  errors,
  updateFormData,
  setErrors,
}) => {
  const [selectedInstitution, setSelectedInstitution] = useState<any>(null);

  useEffect(() => {
    if (formData.universityId) {
      loadInstitutionInfo();
    } else {
      setSelectedInstitution(null);
    }
  }, [formData.universityId]);

  const loadInstitutionInfo = async () => {
    try {
      const institutions = await agentApi.getApprovedInstitutions();
      const institution = institutions.find(inst => inst.id === formData.universityId);
      setSelectedInstitution(institution || null);
    } catch (error) {
      console.error('Error loading institution info:', error);
      setSelectedInstitution(null);
    }
  };

  const handleChange = (field: keyof ApplicationFormData, value: any) => {
    updateFormData({ [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  const institutionCountry = selectedInstitution?.country || 'New Zealand';

  return (
    <div>
      <h5 className="fw-bold mb-3">C. CONTACT DETAILS</h5>
      
      {/* Home Country Contact */}
      <div className="card border mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0 fw-semibold">
            <Home size={18} className="me-2" />
            Home Country Contact
          </h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-12 mb-3">
              <label className="form-label fw-semibold">
                Address Line <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <MapPin size={18} className="text-muted" />
                </span>
                <input
                  type="text"
                  className={`form-control ${errors.homeAddressLine ? 'is-invalid' : ''}`}
                  value={formData.homeAddressLine || ''}
                  onChange={(e) => handleChange('homeAddressLine', e.target.value)}
                  placeholder="Street address"
                />
                {errors.homeAddressLine && (
                  <div className="invalid-feedback">{errors.homeAddressLine}</div>
                )}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">
                City / Town <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.homeCity ? 'is-invalid' : ''}`}
                value={formData.homeCity || ''}
                onChange={(e) => handleChange('homeCity', e.target.value)}
                placeholder="City or town"
              />
              {errors.homeCity && (
                <div className="invalid-feedback">{errors.homeCity}</div>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">
                State <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.homeState ? 'is-invalid' : ''}`}
                value={formData.homeState || ''}
                onChange={(e) => handleChange('homeState', e.target.value)}
                placeholder="State or province"
              />
              {errors.homeState && (
                <div className="invalid-feedback">{errors.homeState}</div>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="application-home-country" className="form-label fw-semibold">
                Country <span className="text-danger">*</span>
              </label>
              <SearchableCountryDropdown
                id="application-home-country"
                name="homeCountrySearchQuery"
                autoComplete="new-password"
                label="Country"
                showFlags
                required
                value={formData.homeCountry || ''}
                onChange={(value) => handleChange('homeCountry', value)}
                error={!!errors.homeCountry}
                describedBy={errors.homeCountry ? 'application-home-country-error' : undefined}
              />
              {errors.homeCountry && (
                <div id="application-home-country-error" className="invalid-feedback d-block">{errors.homeCountry}</div>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">
                Zip / Postal Code <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.homeZipCode ? 'is-invalid' : ''}`}
                value={formData.homeZipCode || ''}
                onChange={(e) => handleChange('homeZipCode', e.target.value)}
                placeholder="Postal or ZIP code"
              />
              {errors.homeZipCode && (
                <div className="invalid-feedback">{errors.homeZipCode}</div>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">
                Home Phone <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <Phone size={18} className="text-muted" />
                </span>
                <input
                  type="tel"
                  className={`form-control ${errors.homePhone ? 'is-invalid' : ''}`}
                  value={formData.homePhone || ''}
                  onChange={(e) => handleChange('homePhone', e.target.value)}
                  placeholder="+1234567890"
                />
                {errors.homePhone && (
                  <div className="invalid-feedback">{errors.homePhone}</div>
                )}
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">
                Home Mobile <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <Phone size={18} className="text-muted" />
                </span>
                <input
                  type="tel"
                  className={`form-control ${errors.homeMobile ? 'is-invalid' : ''}`}
                  value={formData.homeMobile || ''}
                  onChange={(e) => handleChange('homeMobile', e.target.value)}
                  placeholder="+1234567890"
                />
                {errors.homeMobile && (
                  <div className="invalid-feedback">{errors.homeMobile}</div>
                )}
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">Home Fax</label>
              <input
                type="tel"
                className="form-control"
                value={formData.homeFax || ''}
                onChange={(e) => handleChange('homeFax', e.target.value)}
                placeholder="Fax number"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Study Location Contact */}
      <div className="card border mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0 fw-semibold">
            <Building size={18} className="me-2" />
            Study Location Contact (If currently in {institutionCountry})
          </h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-12 mb-3">
              <label className="form-label fw-semibold">Study Address</label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <MapPin size={18} className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  value={formData.studyAddress || ''}
                  onChange={(e) => handleChange('studyAddress', e.target.value)}
                  placeholder={`Address while studying in ${institutionCountry}`}
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">Study Phone</label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <Phone size={18} className="text-muted" />
                </span>
                <input
                  type="tel"
                  className="form-control"
                  value={formData.studyPhone || ''}
                  onChange={(e) => handleChange('studyPhone', e.target.value)}
                  placeholder="+64..."
                />
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">Study Mobile</label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <Phone size={18} className="text-muted" />
                </span>
                <input
                  type="tel"
                  className="form-control"
                  value={formData.studyMobile || ''}
                  onChange={(e) => handleChange('studyMobile', e.target.value)}
                  placeholder="+64..."
                />
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">Study Fax</label>
              <input
                type="tel"
                className="form-control"
                value={formData.studyFax || ''}
                onChange={(e) => handleChange('studyFax', e.target.value)}
                placeholder="Fax number"
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Study Accommodation Type</label>
              <select
                className="form-select"
                value={formData.studyAccommodationType || ''}
                onChange={(e) => handleChange('studyAccommodationType', e.target.value)}
              >
                <option value="">Select accommodation type</option>
                {ACCOMMODATION_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Next of Kin/Parents in Home Country */}
      <div className="card border mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0 fw-semibold">
            <Users size={18} className="me-2" />
            Next of Kin/Parents in Home Country
          </h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">
                Next of Kin Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.nextOfKinName ? 'is-invalid' : ''}`}
                value={formData.nextOfKinName || ''}
                onChange={(e) => handleChange('nextOfKinName', e.target.value)}
                placeholder="Full name"
              />
              {errors.nextOfKinName && (
                <div className="invalid-feedback">{errors.nextOfKinName}</div>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">
                Next of Kin Email <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <Mail size={18} className="text-muted" />
                </span>
                <input
                  type="email"
                  className={`form-control ${errors.nextOfKinEmail ? 'is-invalid' : ''}`}
                  value={formData.nextOfKinEmail || ''}
                  onChange={(e) => handleChange('nextOfKinEmail', e.target.value)}
                  placeholder="email@example.com"
                />
              </div>
              {errors.nextOfKinEmail && (
                <div className="invalid-feedback d-block">{errors.nextOfKinEmail}</div>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">
                Next of Kin Phone <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <Phone size={18} className="text-muted" />
                </span>
                <input
                  type="tel"
                  className={`form-control ${errors.nextOfKinPhone ? 'is-invalid' : ''}`}
                  value={formData.nextOfKinPhone || ''}
                  onChange={(e) => handleChange('nextOfKinPhone', e.target.value)}
                  placeholder="+1234567890"
                />
              </div>
              {errors.nextOfKinPhone && (
                <div className="invalid-feedback d-block">{errors.nextOfKinPhone}</div>
              )}
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">
                Next of Kin Mobile <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <Phone size={18} className="text-muted" />
                </span>
                <input
                  type="tel"
                  className={`form-control ${errors.nextOfKinMobile ? 'is-invalid' : ''}`}
                  value={formData.nextOfKinMobile || ''}
                  onChange={(e) => handleChange('nextOfKinMobile', e.target.value)}
                  placeholder="+1234567890"
                />
              </div>
              {errors.nextOfKinMobile && (
                <div className="invalid-feedback d-block">{errors.nextOfKinMobile}</div>
              )}
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">Next of Kin Fax</label>
              <input
                type="tel"
                className="form-control"
                value={formData.nextOfKinFax || ''}
                onChange={(e) => handleChange('nextOfKinFax', e.target.value)}
                placeholder="Fax number"
              />
            </div>
          </div>

          <div className="row">
            <div className="col-12 mb-3">
              <label className="form-label fw-semibold">
                Next of Kin Address <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <MapPin size={18} className="text-muted" />
                </span>
                <input
                  type="text"
                  className={`form-control ${errors.nextOfKinAddress ? 'is-invalid' : ''}`}
                  value={formData.nextOfKinAddress || ''}
                  onChange={(e) => handleChange('nextOfKinAddress', e.target.value)}
                  placeholder="Full address"
                />
              </div>
              {errors.nextOfKinAddress && (
                <div className="invalid-feedback d-block">{errors.nextOfKinAddress}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* New Zealand Emergency Contact */}
      <div className="card border mb-3">
        <div className="card-header bg-light">
          <h6 className="mb-0 fw-semibold">
            <AlertCircle size={18} className="me-2" />
            {institutionCountry} Emergency Contact
          </h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Emergency Contact Name</label>
              <input
                type="text"
                className="form-control"
                value={formData.emergencyContactName || ''}
                onChange={(e) => handleChange('emergencyContactName', e.target.value)}
                placeholder="Full name"
              />
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label fw-semibold">Emergency Contact Email</label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <Mail size={18} className="text-muted" />
                </span>
                <input
                  type="email"
                  className="form-control"
                  value={formData.emergencyContactEmail || ''}
                  onChange={(e) => handleChange('emergencyContactEmail', e.target.value)}
                  placeholder="email@example.com"
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">Emergency Contact Phone</label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <Phone size={18} className="text-muted" />
                </span>
                <input
                  type="tel"
                  className="form-control"
                  value={formData.emergencyContactPhone || ''}
                  onChange={(e) => handleChange('emergencyContactPhone', e.target.value)}
                  placeholder="+64..."
                />
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label fw-semibold">Emergency Contact Mobile</label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <Phone size={18} className="text-muted" />
                </span>
                <input
                  type="tel"
                  className="form-control"
                  value={formData.emergencyContactMobile || ''}
                  onChange={(e) => handleChange('emergencyContactMobile', e.target.value)}
                  placeholder="+64..."
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12 mb-3">
              <label className="form-label fw-semibold">Emergency Contact Address</label>
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <MapPin size={18} className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  value={formData.emergencyContactAddress || ''}
                  onChange={(e) => handleChange('emergencyContactAddress', e.target.value)}
                  placeholder={`Address in ${institutionCountry}`}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Legacy fields - COMMENTED OUT */}
      {/* <div className="alert alert-info">
        <small>
          <strong>Note:</strong> The above sections cover all contact details. 
          The fields below are legacy fields maintained for compatibility.
        </small>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label fw-semibold">Phone Number (Legacy)</label>
          <input
            type="tel"
            className="form-control"
            value={formData.phoneNumber || ''}
            onChange={(e) => handleChange('phoneNumber', e.target.value)}
            placeholder="+1234567890"
          />
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label fw-semibold">Address (Legacy)</label>
          <input
            type="text"
            className="form-control"
            value={formData.address || ''}
            onChange={(e) => handleChange('address', e.target.value)}
            placeholder="Street address"
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-4 mb-3">
          <label className="form-label fw-semibold">City (Legacy)</label>
          <input
            type="text"
            className="form-control"
            value={formData.city || ''}
            onChange={(e) => handleChange('city', e.target.value)}
            placeholder="City"
          />
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label fw-semibold">Province/State (Legacy)</label>
          <input
            type="text"
            className="form-control"
            value={formData.province || ''}
            onChange={(e) => handleChange('province', e.target.value)}
            placeholder="Province or State"
          />
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label fw-semibold">Country (Legacy)</label>
          <select
            className="form-select"
            value={formData.country || ''}
            onChange={(e) => handleChange('country', e.target.value)}
          >
            <option value="">Select country</option>
            {COUNTRIES.map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label fw-semibold">Postal Code (Legacy)</label>
          <input
            type="text"
            className="form-control"
            value={formData.postalCode || ''}
            onChange={(e) => handleChange('postalCode', e.target.value)}
            placeholder="Postal/ZIP code"
          />
        </div>
      </div> */}
    </div>
  );
};
